import { useState } from 'react';
// Тип контракта живёт в types/, а не в компоненте: три копии уже разошлись —
// в telegram.ts не было mode/strategy, и trace-viewer их не типизировал.
import type { GenerationContext, ObserverSignals } from '../../types';
export type { GenerationContext, ObserverSignals };

interface TraceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  proposalId: string;
  generationContext: GenerationContext | null;
  observerSignals: ObserverSignals | null;
  shadowDecision: string | null;
  createdAt: string;
}

function Section({ title, children, defaultOpen = false }: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-border-subtle rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 bg-surface-elevated flex items-center justify-between text-sm font-medium text-text-secondary hover:bg-bg-hover transition-colors"
      >
        {title}
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-3 py-2 bg-surface-base text-sm">
          {children}
        </div>
      )}
    </div>
  );
}

function SignalBadge({ name, value }: { name: string; value: number }) {
  const color = value >= 0.7
    ? 'bg-accent-danger/20 text-accent-danger'
    : value >= 0.4
    ? 'bg-accent-warning/20 text-accent-warning'
    : 'bg-surface-subtle text-text-secondary';

  return (
    <span className={`px-2 py-0.5 rounded text-xs ${color}`}>
      {name}: {(value * 100).toFixed(0)}%
    </span>
  );
}

export function TraceDrawer({
  isOpen,
  onClose,
  proposalId,
  generationContext,
  observerSignals,
  shadowDecision,
  createdAt,
}: TraceDrawerProps): JSX.Element | null {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="relative w-full max-w-md bg-surface-subtle border-l border-border-subtle overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-surface-subtle border-b border-border-subtle px-4 py-3 flex items-center justify-between">
          <h3 className="font-medium text-text-primary">Trace Viewer</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-bg-hover rounded transition-colors"
          >
            <svg className="w-5 h-5 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Meta info */}
          <div className="bg-surface-elevated rounded-lg p-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-text-secondary">Proposal ID</span>
              <span className="text-text-secondary font-mono text-xs">{proposalId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Generated</span>
              <span className="text-text-secondary">{new Date(createdAt).toLocaleString('ru-RU')}</span>
            </div>
            {generationContext && (
              <>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Model</span>
                  <span className="text-text-secondary font-mono text-xs">{generationContext.model}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Latency</span>
                  <span className="text-text-secondary">{generationContext.latencyMs}ms</span>
                </div>
              </>
            )}
            {shadowDecision && (
              <div className="flex justify-between">
                <span className="text-text-secondary">Decision</span>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  shadowDecision === 'allow' ? 'bg-accent-success/20 text-accent-success' :
                  shadowDecision === 'soften' ? 'bg-accent-warning/20 text-accent-warning' :
                  shadowDecision === 'block' ? 'bg-accent-danger/20 text-accent-danger' :
                  'bg-surface-subtle text-text-secondary'
                }`}>
                  {shadowDecision}
                </span>
              </div>
            )}
          </div>

          {/* Source Turn — the client message we're responding to */}
          {generationContext?.sourceTurn && (
            <Section title="Source Turn (client input)" defaultOpen={true}>
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-text-secondary">Turn ID</span>
                  <span className="text-text-secondary font-mono">{generationContext.sourceTurn.id.slice(0, 12)}...</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-text-secondary">Closed At</span>
                  <span className="text-text-secondary">{new Date(generationContext.sourceTurn.closedAt).toLocaleString('ru-RU')}</span>
                </div>
                <div className="p-2 rounded bg-accent-brand/10 border-l-2 border-accent-brand">
                  <div className="text-text-secondary text-xs whitespace-pre-wrap">
                    {generationContext.sourceTurn.text}
                  </div>
                </div>
              </div>
            </Section>
          )}

          {/* Observer Signals */}
          {observerSignals && Object.keys(observerSignals).length > 0 && (
            <Section title="Observer Signals" defaultOpen={true}>
              <div className="flex flex-wrap gap-2">
                {Object.entries(observerSignals).map(([name, value]) => (
                  value !== undefined && (
                    <SignalBadge key={name} name={name} value={value} />
                  )
                ))}
              </div>
            </Section>
          )}

          {/* Conversation Mode (Phase 3.5) */}
          {generationContext?.mode && (
            <Section title="Conversation Mode" defaultOpen={true}>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Mode</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    generationContext.mode.mode === 'support' ? 'bg-accent-brand/20 text-accent-brand' :
                    generationContext.mode.mode === 'banter' ? 'bg-accent-success/20 text-accent-success' :
                    generationContext.mode.mode === 'conflict' ? 'bg-accent-danger/20 text-accent-danger' :
                    generationContext.mode.mode === 'meta_test' ? 'bg-accent-ai/20 text-accent-ai' :
                    'bg-surface-subtle text-text-secondary'
                  }`}>
                    {generationContext.mode.mode}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Confidence</span>
                  <span className="text-text-secondary">{Math.round(generationContext.mode.confidence * 100)}%</span>
                </div>
                {generationContext.mode.triggeredRules.length > 0 && (
                  <div>
                    <span className="text-text-secondary text-xs">Rules:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {generationContext.mode.triggeredRules.map((rule, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded bg-surface-elevated text-xs text-text-secondary">
                          {rule}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Conversation Intent (Phase 3.3) */}
          {generationContext?.intent && (
            <Section title="Conversation Intent" defaultOpen={true}>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Stage</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    generationContext.intent.stage === 'conversion' ? 'bg-accent-success/20 text-accent-success' :
                    generationContext.intent.stage === 'intent' ? 'bg-accent-brand/20 text-accent-brand' :
                    generationContext.intent.stage === 'trust-building' ? 'bg-accent-warning/20 text-accent-warning' :
                    'bg-surface-subtle text-text-secondary'
                  }`}>
                    {generationContext.intent.stage}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Confidence</span>
                  <span className="text-text-secondary">{Math.round(generationContext.intent.confidence * 100)}%</span>
                </div>
                {generationContext.intent.stageShift !== 'same' && (
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Shift</span>
                    <span className={`text-xs ${
                      generationContext.intent.stageShift === 'up' ? 'text-accent-success' : 'text-accent-danger'
                    }`}>
                      {generationContext.intent.stageShift === 'up' ? '↑' : '↓'}
                    </span>
                  </div>
                )}
                {generationContext.intent.triggeredRules.length > 0 && (
                  <div>
                    <span className="text-text-secondary text-xs">Rules:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {generationContext.intent.triggeredRules.map((rule, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded bg-surface-elevated text-xs text-text-secondary">
                          {rule}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="p-2 rounded bg-accent-ai/10 border-l-2 border-accent-ai mt-2">
                  <div className="text-xs text-text-secondary mb-1">Recommendation:</div>
                  <div className="text-text-secondary text-xs">
                    {generationContext.intent.recommendation}
                  </div>
                </div>
              </div>
            </Section>
          )}

          {/* Response Strategy (Phase 3.6) */}
          {generationContext?.strategy && (
            <Section title="Response Strategy" defaultOpen={true}>
              <div className="space-y-2">
                {/* P0 Risk Alert: askHuman */}
                {generationContext.strategy.askHuman && (
                  <div className="p-2 rounded bg-accent-danger/20 border border-accent-danger/50 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-accent-danger text-sm font-medium">Требуется человек</span>
                    </div>
                    <div className="text-xs text-red-300 mt-1">
                      Обнаружены маркеры риска. Не отправляйте автоматически.
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Strategy</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    generationContext.strategy.selected === 'respond' ? 'bg-accent-brand/20 text-accent-brand' :
                    generationContext.strategy.selected === 'reflect' ? 'bg-accent-warning/20 text-accent-warning' :
                    generationContext.strategy.selected === 'deflect' ? 'bg-accent-warning/20 text-accent-warning' :
                    generationContext.strategy.selected === 'meta' ? 'bg-accent-ai/20 text-accent-ai' :
                    generationContext.strategy.selected === 'silence' ? 'bg-surface-subtle text-text-secondary' :
                    'bg-surface-subtle text-text-secondary'
                  }`}>
                    {generationContext.strategy.selected}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Confidence</span>
                  <span className="text-text-secondary">{Math.round(generationContext.strategy.confidence * 100)}%</span>
                </div>
                {generationContext.turnKind && (
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Turn Kind</span>
                    <span className="px-2 py-0.5 rounded text-xs bg-surface-elevated text-text-secondary">
                      {generationContext.turnKind}
                    </span>
                  </div>
                )}
                {/* Contract enforcement */}
                {generationContext.strategy.contractApplied && (
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Contract Applied</span>
                    <span className="px-2 py-0.5 rounded text-xs bg-accent-warning/20 text-accent-warning">
                      {generationContext.strategy.contractType}
                    </span>
                  </div>
                )}
                {generationContext.strategy.reasonCodes.length > 0 && (
                  <div>
                    <span className="text-text-secondary text-xs">Reason Codes:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {generationContext.strategy.reasonCodes.map((code, i) => (
                        <span key={i} className={`px-1.5 py-0.5 rounded text-xs ${
                          code === 'self_harm_marker' || code === 'self_harm_deflect'
                            ? 'bg-accent-danger/20 text-accent-danger'
                            : 'bg-surface-elevated text-text-secondary'
                        }`}>
                          {code}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Recent Turns */}
          {generationContext?.recentTurns && generationContext.recentTurns.length > 0 && (
            <Section title={`Recent Turns (${generationContext.recentTurns.length})`} defaultOpen={true}>
              <div className="space-y-2">
                {generationContext.recentTurns.map((turn, i) => (
                  <div key={i} className={`p-2 rounded ${
                    turn.role === 'operator' ? 'bg-accent-brand/10 border-l-2 border-accent-brand' : 'bg-surface-subtle'
                  }`}>
                    <div className="text-xs text-text-secondary mb-1">
                      {turn.role === 'operator' ? 'Operator' : 'Client'}
                    </div>
                    <div className="text-text-secondary text-xs whitespace-pre-wrap">
                      {turn.text.length > 200 ? turn.text.slice(0, 200) + '...' : turn.text}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Style Samples */}
          {generationContext?.styleSamples && generationContext.styleSamples.length > 0 && (
            <Section title={`Style Samples (${generationContext.styleSamples.length})`}>
              <div className="space-y-2">
                {generationContext.styleSamples.map((sample, i) => (
                  <div key={i} className="p-2 rounded bg-accent-ai/10 border-l-2 border-accent-ai">
                    <div className="text-text-secondary text-xs whitespace-pre-wrap">
                      "{sample.length > 150 ? sample.slice(0, 150) + '...' : sample}"
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Retrieved Context */}
          {generationContext?.retrievedItems && generationContext.retrievedItems.length > 0 && (
            <Section title={`Retrieved Context (${generationContext.retrievedItems.length})`}>
              <div className="space-y-3">
                {generationContext.retrievedItems.map((item, i) => (
                  <div key={i} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-text-secondary">Score:</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                        item.score >= 0.7 ? 'bg-accent-success/20 text-accent-success' :
                        item.score >= 0.4 ? 'bg-accent-warning/20 text-accent-warning' :
                        'bg-surface-subtle text-text-secondary'
                      }`}>
                        {(item.score * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="p-2 rounded bg-surface-subtle">
                      <div className="text-xs text-text-secondary mb-1">Client:</div>
                      <div className="text-text-secondary text-xs">
                        "{item.clientText.length > 100 ? item.clientText.slice(0, 100) + '...' : item.clientText}"
                      </div>
                    </div>
                    {item.operatorResponse && (
                      <div className="p-2 rounded bg-accent-brand/10 border-l-2 border-accent-brand">
                        <div className="text-xs text-text-secondary mb-1">Operator Response:</div>
                        <div className="text-text-secondary text-xs">
                          "{item.operatorResponse.length > 100 ? item.operatorResponse.slice(0, 100) + '...' : item.operatorResponse}"
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* No context available */}
          {!generationContext && (
            <div className="text-center py-8 text-text-secondary">
              <p className="text-sm">Контекст генерации недоступен</p>
              <p className="text-xs mt-1">Proposal создан до Phase 3.3</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
